import { digObject } from '@domain-group/javascript-utils'

import { useDeepEffect, useSetState } from '@domain-group/react-hooks'

import callApi from '@functions/callApi'

type RequestOptions = {
  agency_id?: number,
  service_provider: string,
}

type UseIntegrationDataOptions = {
  performHttpRequests?: boolean,
  requestOptions: RequestOptions,
}

export type IntegrationData = {
  active_session: boolean,
  id: number,
  integrable: {
    id?: number,
    type?: string,
  },
  new_version: boolean,
  refreshed: boolean,
  service: string,
  token?: string,
}

const defaultState = {
  disconnecting: false,
  integrationData: {} as IntegrationData | {},
  loading: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const loadIntegrationData = async (requestOptions: RequestOptions, setState: SetState) => {
  setState({ loading: true })

  const result = await callApi<IntegrationData>('/integrations/integration_data', requestOptions)
  if (result.success) setState({ integrationData: result.data })

  setState({ loading: false })

  return result
}

const disconnectIntegration = async (integrationData: IntegrationData, setState: SetState) => {
  setState({ disconnecting: true })

  const result = await callApi<IntegrationData>(
    `/integrations/${integrationData.id}/disconnect`,
    {},
    { method: 'POST' },
  )

  if (result.success) setState({ integrationData: result.data })

  setState({ disconnecting: true })

  return result
}

function useIntegrationData(options: UseIntegrationDataOptions) {
  const { performHttpRequests, requestOptions } = options

  const [state, setState] = useSetState(defaultState)
  const { disconnecting, integrationData, loading } = state

  useDeepEffect(() => {
    if (performHttpRequests) loadIntegrationData(requestOptions, setState)
  }, [requestOptions, performHttpRequests])

  const hasActiveSession = digObject(integrationData, 'active_session', false)

  return {
    callbacks: {
      // @ts-expect-error TS2345
      disconnectIntegration: () => disconnectIntegration(integrationData, setState),
      loadIntegrationData: () => loadIntegrationData(requestOptions, setState),
    },
    disconnecting,
    hasActiveSession,
    loading,
    integrationData,
  }
}

export type UseIntegrationDataPayload = ReturnType<typeof useIntegrationData>

export default useIntegrationData
